/**
 * Список ставок раунда.
 *
 * Общий список — главная социальная механика краша: игрок видит, кто ещё в
 * раунде и на каком множителе вышел. После краша показываем итоги: кто забрал,
 * кто слил, пока не откроется следующий раунд.
 */

import { CrashPhase, formatPaws, multiplierToNumber, type CrashBetDto } from "@koshkodrop/shared";

import { useCrash } from "../../store/crash";
import { useSession } from "../../store/session";

/**
 * Ссылка на пустой список обязана быть стабильной: селектор, возвращающий
 * новый массив, заставляет useSyncExternalStore считать состояние изменённым
 * на каждом кадре и уводит компонент в бесконечный перерендер.
 */
const NO_BETS: CrashBetDto[] = [];

function sortBets(bets: CrashBetDto[], settled: boolean): CrashBetDto[] {
  if (!settled) return bets;

  return [...bets].sort((left, right) => {
    const leftWon = left.status === "cashed_out";
    const rightWon = right.status === "cashed_out";
    if (leftWon !== rightWon) return leftWon ? -1 : 1;
    if (leftWon && rightWon) {
      return (right.cashedOutAt ?? 0) - (left.cashedOutAt ?? 0);
    }
    return Number(BigInt(right.stake) - BigInt(left.stake));
  });
}

export function PlayersList() {
  const round = useCrash((state) => state.round);
  const bets = round?.bets ?? NO_BETS;
  const myId = useSession((state) => state.user?.id);
  const settled = round?.phase === CrashPhase.Crashed;
  const visibleBets = sortBets(bets, settled);

  const totalStaked = bets.reduce((sum, bet) => sum + BigInt(bet.stake), 0n);
  const wonCount = bets.filter((bet) => bet.status === "cashed_out").length;
  const lostCount = bets.filter((bet) => bet.status === "lost").length;

  return (
    <div className="rounded-3xl border border-white/5 bg-night-850/60">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-white/40">
          {settled ? "Итоги раунда" : `В раунде: ${bets.length}`}
        </span>
        {settled ? (
          <span className="text-xs font-bold text-white/45">
            <span className="text-win-400">{wonCount} забрали</span>
            <span className="text-white/25"> · </span>
            <span className="text-lose-400">{lostCount} слили</span>
          </span>
        ) : (
          <span className="tabular text-xs font-bold text-paws-400">
            {formatPaws(totalStaked)} 🐾
          </span>
        )}
      </div>

      {visibleBets.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-white/35">Пока никто не поставил</p>
      ) : (
        <ul className="max-h-56 space-y-1 overflow-y-auto px-2 pb-2">
          {visibleBets.map((bet) => {
            const isMine = bet.userId === myId;
            const won = bet.status === "cashed_out";
            const lost = bet.status === "lost";

            return (
              <li
                key={bet.id}
                className={`flex items-center gap-2.5 rounded-2xl px-2.5 py-2 ${
                  isMine ? "bg-paws-500/10" : ""
                }`}
              >
                {bet.photoUrl ? (
                  <img
                    src={bet.photoUrl}
                    alt=""
                    className="size-8 shrink-0 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-night-700 text-sm">
                    🐈
                  </div>
                )}

                <span className="flex-1 truncate text-sm font-semibold text-white/80">
                  {isMine ? "Вы" : (bet.username ?? bet.firstName)}
                </span>

                {won ? (
                  <span className="shrink-0 rounded-lg bg-win-500/15 px-2 py-0.5 text-xs font-bold text-win-400">
                    забрал {multiplierToNumber(bet.cashedOutAt ?? 100).toFixed(2)}x
                  </span>
                ) : lost ? (
                  <span className="shrink-0 rounded-lg bg-lose-500/15 px-2 py-0.5 text-xs font-bold text-lose-400">
                    слил
                  </span>
                ) : (
                  <span className="shrink-0 text-xs font-semibold text-white/30">летит</span>
                )}

                <span
                  className={`tabular w-20 shrink-0 text-right text-sm font-bold ${
                    won ? "text-win-400" : lost ? "text-lose-400" : "text-white/60"
                  }`}
                >
                  {won
                    ? `+${formatPaws(BigInt(bet.payout ?? "0"))}`
                    : lost
                      ? `−${formatPaws(BigInt(bet.stake))}`
                      : formatPaws(BigInt(bet.stake))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
